import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import { getShopifyImage } from 'gatsby-source-shopify';
import { createMetaFieldsMap } from '../../../../../utils';

import BundleSwatchVariant from './BundleSwatchVariant';
import Select from '../../../../Select';

const ns = `bundle-item`;

const BundleItem = ({ item, setBundleListOverflow, setBundleSelections }) => {
	const {
		id, title, options, products,
	} = item;
	const rootClassnames = classNames({
		[`${ ns }`]: true,
		[`${ ns }--no-variants`]: item?.variant_type === `no-variant` || item?.variant_type === `unique-variant`,
	});

	const defaultImageHeight = 160;
	const defaultImageWidth = 130;
	const defaultImageLayout = 'fixed';
	const metafields = createMetaFieldsMap(item.metafields);

	const [imageOne, setImageOne] = useState();
	const [imageTwo, setImageTwo] = useState();
	const [description, setDescription] = useState(``);
	const [swatchMap, setSwatchMap] = useState(null);
	const [selectedVariant, setSelectedVariant] = useState(null);
	const [optionName, setOptionName] = useState(null);
	const isMounted = useRef();

	const setProductData = selectedItem => {
		const setImages = images => {
			if (!images || images.length === 0) { return false; }

			return images.forEach((image, index) => {
				if (index === 0) {
					setImageOne(getShopifyImage({
						image,
						width: defaultImageWidth,
						height: defaultImageHeight,
						layout: defaultImageLayout,
					}));
				}
				if (index === 1) {
					setImageTwo(getShopifyImage({
						image,
						width: defaultImageWidth,
						height: defaultImageHeight,
						layout: defaultImageLayout,
					}));
				}
			});
		};

		const handleNoRelatedImages = () => {
			if (selectedItem?.images) { return setImages(selectedItem?.images); }

			return setImages(item?.images);
		};

		// Set the product description

		if (selectedItem?.description
			|| item?.description
			|| metafields?.custom?.short_description) {
			setDescription(selectedItem?.description
				|| item?.description
				|| metafields?.custom?.short_description);
		}

		// Set the name of option if available

		if (options && options.length !== 0 && options[0]?.name !== `` && options[0]?.name !== `Title`) {
			setOptionName(options[0].name);
		}

		// Find related images and display those if they exist

		if (item?.media && selectedItem?.title) {
			const relatedImages = item.media.filter(_media => {
				const mediaAlt = _media?.altText ? _media?.altText.toLocaleLowerCase() : null;
				const selectedTitle = selectedItem?.title?.toLocaleLowerCase();

				return (mediaAlt && mediaAlt.includes(selectedTitle) && !mediaAlt.includes(`swatch`));
			}).map(_item => {
				return {
					..._item,
					src: _item.image.url,
					originalSrc: _item.image.url,
					transformedSrc: _item.image.url,
				};
			});

			if (relatedImages && relatedImages.length !== 0) {
				setImages(relatedImages);
			} else {
				handleNoRelatedImages();
			}
		} else {
			handleNoRelatedImages();
		}
	};

	const handleChange = selectedItem => {
		setBundleSelections(selectedItem);
		setBundleListOverflow('auto');

		return setProductData(selectedItem);
	};

	const handleVariantChange = selectedItem => {
		setProductData(selectedItem);

		return setBundleSelections(selectedItem);
	};

	const handleFocus = () => {
		return setBundleListOverflow('hidden');
	};

	const handleBlur = () => {
		return setBundleListOverflow('auto');
	};

	const createSwatchMap = _item => {
		const swatches = _item?.media.filter(_media => { return _media.altText.toLocaleLowerCase().includes(`swatch`); });

		if (swatches) {
			setSwatchMap(swatches.map(_swatch => {
				return {
					key: _swatch?.altText?.split(':')[1].trim().toLocaleLowerCase(),
					value: _swatch?.image?.url,
				};
			}));
		}
	};

	useEffect(() => {
		isMounted.current = true;

		if (products) {
			setBundleSelections(products[0]);
			setProductData(products[0]);
		}

		if (item?.variant_type === `variants` || item?.variant_type === `no-variant`) {
			setBundleSelections(item?.variants[0]);
			setSelectedVariant(item?.variants[0]);
			setProductData(item?.variants[0]);

			if (item?.variant_type === `variants` && item?.media) {
				createSwatchMap(item);
			}
		}

		if (item?.variant_type === `unique-variant`) {
			const preDefinedVariant = item?.variants.find(_variant => {
				return _variant?.id.includes(item.variant_id);
			});

			if (preDefinedVariant) {
				setSelectedVariant(preDefinedVariant);
				setBundleSelections(preDefinedVariant);
				setProductData(preDefinedVariant);
			}
		}

		return () => {
			isMounted.current = false;
		};
	}, []); // eslint-disable-line

	const customStyles = {
		option: () => {
			return {};
		},
	};

	return (
		<div key={id} className={rootClassnames}>
			<div className={`${ ns }__media`}>
				{imageOne && (
					<GatsbyImage image={imageOne} alt={imageOne.alt} loading={'lazy'} />
				)}
				{imageTwo && (
					<GatsbyImage image={imageTwo} alt={imageTwo.alt} loading={'lazy'} />
				)}
			</div>
			<div className={`${ ns }__content`}>
				{(title || item?.title) && (
					<p className={`${ ns }__title`}>{title || item?.title}</p>
				)}
				{optionName && (
					<p className={`${ ns }__option-name`}>
						{optionName}
						&nbsp;-&nbsp;
						{selectedVariant?.title}
					</p>
				)}
				{description && (
					<p className={`${ ns }__description`}>{description}</p>
				)}
				{products && (
					<Select
						className={`${ ns }__select`}
						options={products}
						onChange={handleChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						isPortal
						styles={customStyles}
					/>
				)}
				<div className={`${ ns }__swatches`}>
					{swatchMap && item.variants.map(_option => {
						return (
							<BundleSwatchVariant
								item={_option}
								value={_option?.selectedOptions[0]?.value}
								swatchMap={swatchMap}
								isInStock={_option?.availableForSale}
								handleVariantChange={handleVariantChange}
								selectedVariant={selectedVariant}
								setSelectedVariant={setSelectedVariant}
							/>
						);
					})}
				</div>
			</div>
		</div>
	);
};

export default BundleItem;
